import { definePermissions } from '@eis/core/reporting';

/**
 * Reporting (Data Suite) permission catalog — SDD-024 §10, decided with the owner 2026-11-01 and
 * recorded in `modules/reporting/PERMISSIONS.md`.
 *
 * This file is the ONLY place in the module where permission literals may appear — ESLint
 * enforces that. The same keys are enforced SERVER-SIDE per RPC by dataservice's authz
 * interceptor (`services/dataservice/internal/application/permissions.go`); the console's gates
 * are UX, the service is the boundary, and the two spell one catalog.
 */
export const reportingPermissions = definePermissions({
  /**
   * Reports (tables, pivot tables, charts).
   *
   * `view` — the list, the viewer, the published versions. `create` — a new report, an import
   * and `:duplicate`. `update` / `delete` — the draft, publish, discard and archive of a report the
   * caller AUTHORED (or any, with `manage`). `share` — visibility WORKSPACE. `export` — CSV/XLSX
   * as a job. `manage` — edit, share and archive every report of the workspace, not only one's
   * own (the analyst's; "own" is enforced by the service as `NOT_AUTHOR`).
   */
  reports: {
    domain: 'reporting',
    entity: 'reports',
    actions: ['view', 'create', 'update', 'delete', 'share', 'export', 'manage'],
  },
  /**
   * Datasets: `view` — the grouped catalog of the tenant's warehouse and the custom datasets,
   * and the catalog refresh. `manage` — build, publish and archive custom datasets (joins).
   */
  datasets: {
    domain: 'reporting',
    entity: 'datasets',
    actions: ['view', 'manage'],
  },
  /**
   * Queries: `run` — run a report or sample a dataset at the warehouse (scan cost), poll and page
   * the result. A viewer may run only the versions they may see.
   */
  queries: {
    domain: 'reporting',
    entity: 'queries',
    actions: ['run'],
  },
  /**
   * Workspace settings: `manage` — the default formats and the result-reuse window. Reading them
   * needs only `reports.view` (every renderer uses them). Held by admin alone (§10).
   */
  settings: {
    domain: 'reporting',
    entity: 'settings',
    actions: ['manage'],
  },
});
